const MONTH_NAMES_EN = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];
const MONTH_NAMES_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
const MONTH_ABBR_EN = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

export function normalizeSearchText(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function isSubsequenceSearch(searchTerm: string, text: string): boolean {
  if (!text || !searchTerm) return false;
  const normTerm = normalizeSearchText(searchTerm);
  const normText = normalizeSearchText(text);
  if (normTerm.length === 0) return true;
  if (normTerm.length > normText.length) return false;

  let searchIndex = 0;
  for (let i = 0; i < normText.length && searchIndex < normTerm.length; i++) {
    if (normText[i] === normTerm[searchIndex]) {
      searchIndex++;
    }
  }
  return searchIndex === normTerm.length;
}

export function textMatchesSearch(searchTerm: string, text: string): boolean {
  if (!searchTerm) return true;
  if (!text) return false;
  const normTerm = normalizeSearchText(searchTerm);
  const normText = normalizeSearchText(text);
  return normText.includes(normTerm) || isSubsequenceSearch(normTerm, normText);
}

function getMonthIndicesForSearchTerm(searchTerm: string): number[] {
  const norm = normalizeSearchText(searchTerm);
  if (!norm) return [];

  const indices: number[] = [];
  for (let i = 0; i < 12; i++) {
    const aliases = [MONTH_NAMES_EN[i], MONTH_NAMES_ES[i], MONTH_ABBR_EN[i]];
    const matches = aliases.some(
      (alias) =>
        alias === norm ||
        alias.includes(norm) ||
        norm.includes(alias) ||
        isSubsequenceSearch(norm, alias)
    );
    if (matches) indices.push(i);
  }
  return indices;
}

function contestHasDateInMonths(contest: Record<string, unknown>, months: number[]): boolean {
  if (months.length === 0) return false;

  const dateFields = [
    'submission_start',
    'submission_deadline',
    'voting_start',
    'voting_end',
    'winner_announcement',
    'created_at',
  ];

  for (const field of dateFields) {
    const value = contest[field];
    if (!value) continue;
    const date = new Date(String(value));
    if (!Number.isNaN(date.getTime()) && months.includes(date.getMonth())) {
      return true;
    }
  }
  return false;
}

function parseThemeKeywords(contest: Record<string, unknown>): string[] {
  let keywords = contest.theme_keywords;
  if (!keywords) return [];
  if (typeof keywords === 'string') {
    try {
      keywords = JSON.parse(keywords);
    } catch {
      return [keywords];
    }
  }
  return Array.isArray(keywords) ? keywords.map(String) : [];
}

export function contestMatchesSearch(contest: Record<string, unknown>, searchTerm: string): boolean {
  const term = searchTerm.trim();
  if (!term) return true;

  const searchableFields = [
    contest.name,
    contest.sponsor_name || "Noah's Ark",
    contest.description,
    contest.theme_name,
    ...parseThemeKeywords(contest),
  ]
    .filter(Boolean)
    .map(String);

  for (const field of searchableFields) {
    if (textMatchesSearch(term, field)) return true;
  }

  const monthIndices = getMonthIndicesForSearchTerm(term);
  if (monthIndices.length > 0 && contestHasDateInMonths(contest, monthIndices)) {
    return true;
  }

  const dateFields = [
    'submission_start',
    'submission_deadline',
    'voting_start',
    'voting_end',
    'winner_announcement',
  ];
  for (const field of dateFields) {
    const value = contest[field];
    if (value && textMatchesSearch(term, String(value))) return true;
  }

  return false;
}
